import React, { createContext, useEffect, useState, useRef, useContext } from 'react'
import DeviceLogin from '../components/DeviceLogin'
import { localStorage } from 'okeoke.localstorage'
import { deviceLogin } from '../api/deviceLogin'
import { OkeFrameContext } from './OkeFrameContext'
import { UUID } from "uuidjs"
import { OrderEngineClient, DeviceTypes } from '../orderengine-client/index'
import { brandParams } from '../api/brandParams'

const OkeScreenDeviceContext = createContext()

const getSearchValues = () => {
  let searchParsed = (window.location.search || '').replace('?', '').split('&')
  let searchValues = {}
  for (let val of searchParsed) {
    let [key, value] = val.split('=')
    searchValues[key] = value
  }
  searchValues.previewMode = searchValues?.previewMode === '1' ? true : false
  return searchValues
}

function transcodeID(value, keyName) {
  if (value == null) return {}
  const map = value.reduce(
    (obj, item) => ({
      ...obj,
      [item[keyName]]: { ...item }
    }),
    {}
  )
  return map
}

const OkeScreenDeviceProvider = (props) => {
  const uid = useRef(UUID.generate())
  const searchValues = getSearchValues()
  const { sendMessageToWebView, hasFrameContext, frameType, frameBrandID = null } = useContext(OkeFrameContext)
  const { prefix, children, title, types, onTokenStateDetermined, CustomDeviceLogin = null } = props
  const [hasDeviceContext] = useState(true)
  const [clientUID, setClientUID] = useState(localStorage.getItem('0001-cuid'))
  const [brandID, setBrandID] = useState((!searchValues?.previewMode) ? localStorage.getItem(`okeoke-${prefix || '0'}-device-brand-id`) : null)
  const [deviceUUID, setDeviceUUID] = useState((!searchValues?.previewMode) ? localStorage.getItem(`okeoke-${prefix || '0'}-device-uuid`) : null)
  const [parentDeviceUUID, setParentDeviceUUID] = useState((!searchValues?.previewMode) ? localStorage.getItem(`okeoke-${prefix || '0'}-device-parent-uuid`) || null : null)
  const [deviceType, setDeviceType] = useState((!searchValues?.previewMode) ? localStorage.getItem(`okeoke-${prefix || '0'}-device-type`) : null)
  const [deviceParams, setDeviceParams] = useState((!searchValues?.previewMode) ? JSON.parse(localStorage.getItem(`okeoke-${prefix || '0'}-device-params`) || '{}') : null)
  const [devicePaymentAgents, setDevicePaymentAgents] = useState((!searchValues?.previewMode) ? JSON.parse(localStorage.getItem(`okeoke-${prefix || '0'}-device-pa`) || '{}') : null)
  const [deviceName, setDeviceName] = useState((!searchValues?.previewMode) ? localStorage.getItem(`okeoke-${prefix || '0'}-device-name`) : null)
  const [deviceLocationUUID, setDeviceLocationUUID] = useState((!searchValues?.previewMode) ? localStorage.getItem(`okeoke-${prefix || '0'}-device-location-uuid`) : null)
  const [token, setToken] = useState((!searchValues?.previewMode) ? localStorage.getItem(`okeoke-${prefix || '0'}-device-token`) : null)
  const [tokenType, setTokenType] = useState((!searchValues?.previewMode) ? localStorage.getItem(`okeoke-${prefix || '0'}-device-token-type`) : null)
  const [expires, setExpires] = useState((!searchValues?.previewMode) ? +localStorage.getItem(`okeoke-${prefix || '0'}-device-token-expires`) : null)
  const [apiUrl, setApiUrl] = useState((!searchValues?.previewMode) ? localStorage.getItem(`okeoke-${prefix || '0'}-device-apiurl`) : null)
  const [loading, setLoading] = useState(true)
  const [loggedIn, setLoggedIn] = useState(false)
  const [loggedInUser, setLoggedInUser] = useState(null)
  const [error, setError] = useState(null)
  const [disabled, setDisabled] = useState(false)

  const [requestCode, setRequestCode] = useState('')
  const timeoutRef = useRef(null)
  const codeRef = useRef(null)

  const [client, setClient] = useState(null)
  const [contextInited, setContextInited] = useState(false)

  const [tokenData, setTokenData] = useState(null)
  const [catalogLoading, setCatalogLoading] = useState(true)
  const [catalogUpdated, setCatalogUpdated] = useState(false)
  const [socketConnected, setSocketConnected] = useState(false)
  const [socketAuthenticated, setSocketAuthenticated] = useState(false)
  const [languages, setLanguages] = useState({})
  const [currencies, setCurrencies] = useState({})

  const [deviceState, setDeviceState] = useState({})

  const [appParams, setAppParams] = useState(null)

  const tokenStateDetermined = (valid) => {
    if (!valid && timeoutRef.current == null) {
      timeoutRef.current = setTimeout(checkRemoteLogin, 1000)
    }
    if (valid && timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    if (typeof onTokenStateDetermined === 'function') {
      onTokenStateDetermined(valid)
    }
  }

  const initClientUID = () => {
    let currentUID = localStorage.getItem('0001-cuid')
    if (currentUID == null) {
      const newUID = UUID.generate()
      setClientUID(newUID)
      localStorage.setItem('0001-cuid', newUID)
    } else {
      setClientUID(currentUID)
    }
  }

  useEffect(() => {
    if (apiUrl && prefix) {
      brandParams.getBrandParams(apiUrl, prefix).then(res => {
        if (res.success) {
          setAppParams(res.data.params)
        }
      })
    }
  }, [apiUrl, prefix])

  useEffect(() => {
    initClientUID()
    let loc = window.location
    let splitPath = loc.pathname.split('/')
    let action = (splitPath[splitPath.length - 1] === '' ? splitPath[splitPath.length - 2] : splitPath[splitPath.length - 1])
    if (action === 'initDevice') {
      let apiUrl = `https://${loc.host}`
      let token = searchValues?.t || null
      if (apiUrl != null && token != null) {
        onLogin(token, apiUrl)
        let deviceName = (splitPath[0] === '' ? splitPath[1] : splitPath[0])
        sendMessageToWebView({
          action: 'setAppUrl',
          data: { url: `${apiUrl}/${deviceName}`, url2: null }
        })
        setTimeout(() => window.history.replaceState({}, "Activated", `/${deviceName}`), 1000)
      }
    }
  })

  useEffect(() => {
    if (deviceParams != null) {
      localStorage.setItem(`okeoke-${prefix || '0'}-device-params`, JSON.stringify(deviceParams))
    }
  }, [deviceParams])

  useEffect(() => {
    if (devicePaymentAgents != null) {
      localStorage.setItem(`okeoke-${prefix || '0'}-device-pa`, JSON.stringify(devicePaymentAgents))
    }
  }, [devicePaymentAgents])

  useEffect(() => {
    if (clientUID != null) {
      if (apiUrl != null && token != null && clientUID != null) {
        deviceLogin.checkDevice(apiUrl, token, clientUID).then(checkRes => {
          if (checkRes?.data?.deviceState?.connected && checkRes?.data?.deviceState?.deviceInfo?.clientUID !== clientUID) {
            setLoading(false)
            tokenStateDetermined(false)
            setError({ message: 'Másik készüléken aktiválva lett ez a licensz (1 eszköz egyszerre 1 készüléken lehet aktiválva).' })
          } else {
            deviceLogin.refreshDeviceToken(apiUrl, token).then(res => {
              if (res.success) {
                if (!res.data?.enabled) {
                  setLoading(false)
                  tokenStateDetermined(false)
                  setError({ message: 'Ez az eszköz jelenleg nincs engedélyezve.' })
                } else {
                  //sendMessageToWebView({
                  //  action: 'setAppUrl',
                  //  data: { url: `${apiUrl}/${DeviceTypes[res.data.deviceType]}`, url2: `${apiUrl}/secondScreen` }
                  //})
                  setLoading(false)
                  setBrandID(res.data.brandID)
                  localStorage.setItem(`okeoke-${prefix || '0'}-device-brand-id`, res.data.brandID)
                  setDeviceUUID(res.data.uuid)
                  localStorage.setItem(`okeoke-${prefix || '0'}-device-uuid`, res.data.uuid)
                  setParentDeviceUUID(res.data.parentDeviceUUID)
                  localStorage.setItem(`okeoke-${prefix || '0'}-device-parent-uuid`, res.data.parentDeviceUUID || null)
                  setDeviceType(res.data.deviceType)
                  localStorage.setItem(`okeoke-${prefix || '0'}-device-type`, res.data.deviceType)
                  setDeviceParams(res.data.deviceParams)
                  localStorage.setItem(`okeoke-${prefix || '0'}-device-params`, JSON.stringify(res.data.deviceParams))
                  setDevicePaymentAgents(res.data.paymentAgents)
                  localStorage.setItem(`okeoke-${prefix || '0'}-device-pa`, JSON.stringify(devicePaymentAgents))
                  setDeviceName(res.data.deviceName)
                  localStorage.setItem(`okeoke-${prefix || '0'}-device-name`, res.data.deviceName)
                  setDeviceLocationUUID(res.data.locationUUID)
                  localStorage.setItem(`okeoke-${prefix || '0'}-device-location-uuid`, res.data.locationUUID)
                  setExpires(res.expires)
                  localStorage.setItem(`okeoke-${prefix || '0'}-device-token-expires`, res.expires)
                  localStorage.setItem(`okeoke-${prefix || '0'}-device-token`, res.token)
                  setTokenType(res.type)
                  localStorage.setItem(`okeoke-${prefix || '0'}-device-token-type`, res.type)
                  setLoggedIn(true)
                  tokenStateDetermined(true)
                }
              } else {
                setLoading(false)
                if (res.code === 500 && expires > 0) {
                  var expiresDate = new Date(expires * 1000)
                  var currentDate = new Date()
                  if (expiresDate.getTime() > currentDate.getTime()) {
                    setLoggedIn(true)
                    tokenStateDetermined(true)
                  } else {
                    tokenStateDetermined(false)
                    localStorage.clear()
                  }
                } else {
                  tokenStateDetermined(false)
                }
              }
            })
          }
        })
      } else {
        setLoading(false)
        tokenStateDetermined(false)
      }
      return () => {
        // ON UNMOUNT
      }
    }
  }, [apiUrl, token, clientUID])

  const onRemoteLogin = (token, apiUrl, app) => {
    let appName = DeviceTypes[app]
    if (window.location.host === 'activate.app.barsoft.hu') {
      window.location.replace(`${apiUrl}/${appName}/initDevice?t=${token}`);
    } else {
      localStorage.setItem(`okeoke-${appName || 'na'}-device-token`, token)
      localStorage.setItem(`okeoke-${appName || 'na'}-device-apiurl`, apiUrl)
      window.location.reload()
    }
  }

  const checkRemoteLogin = () => {
    deviceLogin.remoteDeviceLogin(codeRef.current).then(res => {
      if (res.success) {
        codeRef.current = res.data.code
        setRequestCode(codeRef.current)
        if (res.data.auth?.token != null) {
          if (types.includes(res.data.auth?.deviceType)) {
            onRemoteLogin(res.data.auth?.token, res.data.auth?.apiUrl, res?.data?.auth?.deviceType)
            setError(null)
          } else {
            setError({ message: 'Accepted: ' + types.map(ty => ' ' + DeviceTypes[ty]) + ' Requested: ' + DeviceTypes[res?.data?.auth?.deviceType] })
          }
        }
      }
      timeoutRef.current = setTimeout(checkRemoteLogin, 5000)
    })
  }

  const checkPin = (pin) => {
    if (pin && pin.length >= 4) {
      setError(null)
      deviceLogin.generateDeviceToken(pin).then(res => {
        if (res.success) {
          if (types.includes(res?.data?.info?.deviceType)) {
            onRemoteLogin(res.data.token, res.data.info.apiUrl, res?.data?.info.deviceType)
            setError(null)
          } else {
            setError({ message: 'Accepted: ' + types.map(ty => ' ' + DeviceTypes[ty]) + ' Requested: ' + DeviceTypes[res?.data?.info?.deviceType] })
          }
        } else {
          setError(res)
        }
      }).catch(err => {
        console.log(err)
        setError({ message: 'Something went wrong. Is the device connected?' })
      })
    }
  }

  const onLogin = (token, apiUrl) => {
    setToken(token)
    setApiUrl(apiUrl)
    localStorage.setItem(`okeoke-${prefix || '0'}-device-token`, token)
    localStorage.setItem(`okeoke-${prefix || '0'}-device-apiurl`, apiUrl)
  }

  const logoffDevice = () => {
    localStorage.clear()
    setLoggedIn(false)
    window.location.reload()
  }

  const onConnectionChange = async (data) => {
    switch (data.state) {
      case 'Connected': setSocketConnected(true)
        break
      case 'Disconnected': setSocketConnected(false)
        break
      case 'Authorized': setSocketAuthenticated(true)
        if (loggedInUser != null) {
          client.setDeviceUser(loggedInUser.userUUID, loggedInUser.pinCode)
        }
        // Bind the client to the device locationUUID
        setTokenData(data.data.data)
        getDevicesStates()
        break
      case 'Unauthorized': setSocketAuthenticated(false)
        break
      default:
    }
  }

  const onCatalogStateChange = (data) => {
    setCatalogLoading(data.state !== 'Loaded')
  }

  const onDeviceForceLogout = () => {
    logoffDevice()
  }

  const onDeviceDisableDuplicate = () => {
    client?.disable()
    setDisabled(true)
  }

  const onCatalogUpdated = () => {
    setCatalogUpdated(true)
  }

  const onAppParamsUpdated = (data) => {
    if (data.app === prefix) setAppParams(data.params.combined)
    else {
      if (data.app === 'base') {
        brandParams.getBrandParams(apiUrl, prefix).then(res => {
          if (res.success) {
            setAppParams(res.data.params)
          }
        })
      }
    }
  }

  const onDeviceParamsUpdated = (data) => {
    if (data?.uuid === deviceUUID && data.deviceParams != null) {
      setDeviceParams({ ...data.deviceParams })
      setDevicePaymentAgents([...data.paymentAgents])
    }
  }

  const onDeviceStateUpdated = (data) => {
    if (data?.uuid === deviceUUID) {
      setDeviceState({ ...data.deviceState })
    }
  }

  const getDevicesStates = () => {
    return new Promise((resolve, reject) => {
      let finished = false
      setTimeout(() => {
        if (!finished) {
          finished = true
          resolve({ success: false, message: 'Timeout!', code: 408, data: { errMsgCode: 'request.error.timeout' } })
        }
      }, 5000)
      client?.clientSocket.ioclient.emit('getDevicesStates', {}, (data) => {
        if (!finished) {
          finished = true
          let states = transcodeID(data.data, 'uuid')
          if (data.success) setDeviceState(states[deviceUUID] || {})
          resolve(data)
        }
      })
    })
  }

  useEffect(() => {
    if (apiUrl && brandID && ((deviceParams && clientUID))) {
      if (client == null) {
        if ((hasFrameContext) || (!hasFrameContext)) {
          let activeClient = new OrderEngineClient(
            apiUrl,
            'secondScreen',
            token,
            { frameType },
            deviceParams,
            clientUID,
            sendMessageToWebView
          )
          activeClient.init()
          setClient(activeClient)
          setContextInited(true)
        }
      } else {
        client.setToken(token)
      }
    }
  }, [apiUrl, token, brandID, hasFrameContext, frameType, clientUID, deviceParams])

  useEffect(() => {
    if (client) {
      setSocketConnected(client.clientSocket.connected)
      setSocketAuthenticated(client.clientSocket.authorized)
      setCatalogLoading(client.catalogLoading == null ? true : client.catalogLoading)
      setLanguages(client.catalog?.languages || null)
      setCurrencies(client.catalog?.currencies || null)
      setTokenData(client.tokenData)

      client.addCallback('onConnectionChange', uid.current, onConnectionChange)
      client.addCallback('onCatalogStateChange', uid.current, onCatalogStateChange)
      client.clientSocket.addCallback('onDeviceForceLogout', uid.current, onDeviceForceLogout)
      client.clientSocket.addCallback('onDeviceDisableDuplicate', uid.current, onDeviceDisableDuplicate)
      client.clientSocket.addCallback('onCatalogUpdated', uid.current, onCatalogUpdated)
      client.clientSocket.addCallback('onAppParamsUpdated', uid.current, onAppParamsUpdated)
      client.clientSocket.addCallback('deviceParamsUpdated', uid.current, onDeviceParamsUpdated)
      client.clientSocket.addCallback('onDeviceStateUpdated', uid.current, onDeviceStateUpdated)
    }
    return () => {
      // ON UNMOUNT
      if (client != null) {
        client.removeCallback('onConnectionChange', uid.current)
        client.removeCallback('onCatalogStateChange', uid.current)
        client.clientSocket.removeCallback('onDeviceForceLogout', uid.current)
        client.clientSocket.removeCallback('onDeviceDisableDuplicate', uid.current)
        client.clientSocket.removeCallback('onCatalogUpdated', uid.current)
        client.clientSocket.removeCallback('onAppParamsUpdated', uid.current)
        client.clientSocket.removeCallback('deviceParamsUpdated', uid.current)
        client.clientSocket.removeCallback('onDeviceStateUpdated', uid.current)
      }
    }
  }, [client])

  return (
    <OkeScreenDeviceContext.Provider
      value={{
        hasDeviceContext, logoffDevice, loggedIn, brandID,
        deviceUUID, deviceType, deviceParams, setDeviceParams,
        deviceName, deviceLocationUUID,
        expires, token, tokenType, apiUrl, prefix, loggedInUser,
        setLoggedInUser, clientUID, setDisabled, sendMessageToWebView, contextInited,
        client, socketConnected, socketAuthenticated, catalogLoading, languages, currencies,
        tokenData, catalogUpdated, deviceState,
        parentDeviceUUID, appParams
      }}
    >
      {!disabled && !loading && !loggedIn && (CustomDeviceLogin != null ?
        <CustomDeviceLogin onLogin={onLogin} onRemoteLogin={onRemoteLogin} title={title} types={types} error={error} requestCode={requestCode} checkPin={checkPin} /> :
        <DeviceLogin onLogin={onLogin} onRemoteLogin={onRemoteLogin} title={title} types={types} error={error} requestCode={requestCode} checkPin={checkPin} />)}
      {!disabled && !loading && loggedIn && children}
      {disabled && (CustomDeviceLogin ? <CustomDeviceLogin deviceDisabled={true} /> : <DeviceLogin deviceDisabled={true} />)}
    </OkeScreenDeviceContext.Provider>
  )
}

export { OkeScreenDeviceContext, OkeScreenDeviceProvider }
